import { useRef, useMemo, useEffect } from "react";
import Blaze from "@swiggy-private/blaze.js";
// eslint-disable-next-line import/named
import { init, addRequestInterceptor, addResponseInterceptor, mockWebSdk } from "@swiggy-private/native-bridge";
import { NativeRequest } from "@swiggy-private/native-bridge/dist/interfaces";
import { initBridgeSdk, isBrowserEnv } from "utils/bridgeSdk";
import { useEffectOnce } from "./useEffectOnce";
import { isEmpty } from "@swiggy-private/js-utils";
import packageJson from "../package.json";
import { getAppHostName, getAppLocationHref, getUserAgent } from "utils/newrelic";

// initalise Native Bridge SDK
export const Sdk = init();

const EMPTY_FUNCTION = () => void 0;

const getPrefix = (prefix?: string): string => (!isEmpty(prefix) ? `${prefix}_` : "");

const createNBRequestInterceptor = (recordNREvent?: Function, prefix?: string): VoidFunction =>
    addRequestInterceptor((req: NativeRequest<{ state?: string }>) => {
        if (recordNREvent == null) {
            return;
        }
        const projectPrefix = getPrefix(prefix);

        recordNREvent({
            eventType: `${projectPrefix}web_native_bridge_request`,
            id: req.requestId,
            type: req.type,
            subType: req.payload?.state ?? "",
        });
    });

const createNBResponseInterceptor = (recordNREvent?: Function, prefix?: string): VoidFunction =>
    addResponseInterceptor((res) => {
        if (recordNREvent == null) {
            return;
        }
        const projectPrefix = getPrefix(prefix);

        recordNREvent({
            eventType: `${projectPrefix}web_native_bridge_response`,
            id: res.requestId,
            type: res.type,
            statusCode: res.statusCode ?? -1,
            statusMessage: res.statusMessage ?? "",
            bridgeType: res.bridgeType ?? "",
        });
    });

export function useBootstrap(nbEventTypePrefix: string, isCached?: boolean): void {
    const initRef = useRef(false);

    useEffectOnce(() => {
        if (isBrowserEnv()) {
            mockWebSdk();
        }
    });

    if (!initRef.current) {
        // init blaze
        Blaze.init({
            sendToAnalytics: EMPTY_FUNCTION,
            enableResourceTiming: true,
            nrConfig: {
                enable: true,
                config: {
                    NR_ENDPOINT: "https://insights-collector.newrelic.com/v1/accounts/737486/events",
                    NR_KEY: "e1687c0399396561695f01042f298f764ad2b997",
                },
                meta: {
                    appName: nbEventTypePrefix, // enter app name
                    /* JS hash is generated without replacing with actual value
                So cached file would be same for both canary vs non-canary, and would have unknown behavior */
                    appDeployName: isBrowserEnv()
                        ? (document?.querySelector("[data-app-name]") as HTMLDivElement)?.dataset.appName ?? ""
                        : "",
                    ...(isCached && { isHtmlCached: String(isCached) }),
                    appVersion: packageJson.version,
                    appUserAgent: getUserAgent(),
                    appHostName: getAppHostName(),
                    appLocationHref: getAppLocationHref(),
                },
            },
        });

        // create function to intialise NB
        initBridgeSdk(Sdk);
    }

    const requestInterceptor = useMemo(
        () => createNBRequestInterceptor(Blaze.recordCustomEvent, nbEventTypePrefix),
        [nbEventTypePrefix],
    );
    const responseInterceptor = useMemo(
        () => createNBResponseInterceptor(Blaze.recordCustomEvent, nbEventTypePrefix),
        [nbEventTypePrefix],
    );

    useEffectOnce(() => {
        initRef.current = true;
    });

    useEffect(() => {
        return (): void => {
            requestInterceptor();
            responseInterceptor();
        };
    }, [requestInterceptor, responseInterceptor]);
}
